import React from 'react'

import { Col, Hr, Row, Spacer } from '@amzn/stencil-react-components/layout'
import { useBreakpoints } from '@amzn/stencil-react-components/responsive'
import { H4 } from '@amzn/stencil-react-components/text'

import { ItemEditorBasicInfo } from 'src/components/ItemEditorBasicInfo'
import { ResponsiveRow } from 'src/components/ResponsiveRow'
import { isDefaultLocale } from 'src/contexts/ModuleLocaleContext'
import { useItemEntity, useValidationErrorEntity } from 'src/hooks/useEntity'
import { WritingExerciseItemDTO } from 'src/models/dto/items/WritingExerciseItemDTO'
import { ItemEditorProps } from 'src/pages/module-builder/item-editors/ItemEditorContainer'
import {
    ItemEditorNumberInput,
    ItemEditorTextArea,
    ItemEditorTextInput,
    LabelSize,
} from 'src/pages/module-builder/item-editors/ItemEditorInputs'
import { ItemEditorValidationErrors } from 'src/pages/module-builder/item-editors/ItemEditorValidationErrors'
import { WritingExerciseHandler } from 'src/services/EntityServices/ItemUpdateHandlers/WritingExerciseHandler'

export const WritingExerciseItemEditor = ({
    itemId,
    locale,
    workflowEntityId,
    showValidationError,
    editDisabled,
}: ItemEditorProps) => {
    const { entity: itemDTO } = useItemEntity<WritingExerciseItemDTO>({
        entityId: itemId,
        workflowEntityId,
    })

    const { entity: validationErrorEntity } = useValidationErrorEntity(itemId)

    const { matches } = useBreakpoints()

    const flexItemWidth = matches.s ? '100%' : 'default'

    return (
        <>
            <ItemEditorValidationErrors {...{ itemId, showValidationError }} />
            <ItemEditorBasicInfo
                handler={WritingExerciseHandler}
                itemId={itemId}
                workflowEntityId={workflowEntityId}
                locale={locale}
                editDisabled={editDisabled}
                showValidationError={showValidationError}
            />
            <Spacer height={'S200'} />
            <Row alignItems={'center'}>
                <Col flexGrow={1} width={flexItemWidth}>
                    <ItemEditorTextInput
                        dataTestId={'title'}
                        inputId={'title'}
                        disabled={editDisabled}
                        value={itemDTO.titleI18N[locale] ?? ''}
                        placeholder={'Title of Instructions'}
                        itemId={itemDTO.id}
                        setValue={(nextValue: string) => {
                            WritingExerciseHandler.updateTitle(itemDTO.id, locale, nextValue)
                        }}
                        validationErrorMessage={(
                            validationErrorEntity.validationErrors.titleI18N ?? []
                        ).join(', ')}
                        showError={showValidationError}
                        labelText={'Writing exercise instruction title (optional)'}
                    />
                </Col>
            </Row>
            <Spacer height={'S200'} />
            <Row alignItems={'center'}>
                <Col flexGrow={1} width={flexItemWidth}>
                    <ItemEditorTextArea
                        locale={locale}
                        dataTestId={'instructions'}
                        inputId={'instructions'}
                        disabled={editDisabled}
                        value={itemDTO.instructionI18N[locale] ?? ''}
                        itemId={itemDTO.id}
                        setValue={(nextValue: string) => {
                            WritingExerciseHandler.updateInstruction(itemDTO.id, locale, nextValue)
                        }}
                        validationErrorMessage={(
                            validationErrorEntity.validationErrors.instructionI18N ?? []
                        ).join(', ')}
                        showError={showValidationError}
                        labelText={'Writing exercise instructions'}
                        labelSize={LabelSize.Small}
                        placeholder={'Instructions for writing exercise'}
                    />
                </Col>
            </Row>
            <Spacer height='S400' />
            <Hr />
            <Spacer height='S200' />
            <Row alignItems={'center'}>
                <H4>WYSIWIG settings</H4>
            </Row>
            <ResponsiveRow alignItems={'center'} gridGap={'S400'}>
                <Col width={matches.s ? '100%' : 'default'}>
                    <ItemEditorNumberInput
                        dataTestId={'max-character-limit'}
                        inputId={'max-character-limit'}
                        itemId={itemDTO.id}
                        labelText='Maximum Character Limit'
                        disabled={!isDefaultLocale(locale) || editDisabled}
                        value={itemDTO.wordLimit === 0 ? null : itemDTO.wordLimit}
                        placeholder='8000'
                        validationErrorMessage={(
                            validationErrorEntity.validationErrors.wordLimit ?? []
                        ).join(', ')}
                        showError={showValidationError}
                        setValue={(nextValue: number | null) => {
                            WritingExerciseHandler.updateWordLimit(itemDTO.id, nextValue)
                        }}
                    />
                </Col>
                <Col width={matches.s ? '100%' : 'default'}>
                    <ItemEditorNumberInput
                        dataTestId={'min-characters-allowed'}
                        inputId={'min-characters-allowed'}
                        itemId={itemDTO.id}
                        labelText='Minimum characters allowed (optional)'
                        disabled={!isDefaultLocale(locale) || editDisabled}
                        value={itemDTO.minWordLimit}
                        placeholder='200'
                        validationErrorMessage={(
                            validationErrorEntity.validationErrors.minWordLimit ?? []
                        ).join(', ')}
                        showError={showValidationError}
                        setValue={(nextValue: number | null) => {
                            WritingExerciseHandler.updateMinWordsAllowed(itemDTO.id, nextValue)
                        }}
                    />
                </Col>
            </ResponsiveRow>
        </>
    )
}
